import moment from 'moment'
import { find, propEq, pipe, pick } from 'ramda'
import { useTranslation } from 'react-i18next'
import CurrencyFormatter from '@/components/currency-formatter'
import getPriceDateOptions from '@/utils/price-date-options'
import { colors } from '@/styles'
import {
  ContentWrapper,
  AreaTitle,
  StyledPanel,
  StyledRow,
  StyledLabel,
  StyledValue
} from './elements'
import { PaymentData } from '../types'

type PaymentSummaryProps = {
  paymentData: PaymentData
  listingId: string
}

const formatName = (userInfo: any) => `${userInfo.first_name} ${userInfo.last_name}`

const getFullName = pipe(pick(['first_name', 'last_name']), formatName)

const PaymentSummary: React.FC<PaymentSummaryProps> = ({ paymentData, listingId }) => {
  const {
    charged,
    payment_status,
    description,
    pad_agreement,
    transaction_number,
    payment_proccess_at,
    tenant = {},
    user = {}
  } = paymentData
  const { t } = useTranslation()

  const paymentStates = {
    upcoming: t('b.payment.view.payment_status_upcoming.text', 'Upcoming'),
    collecting_funds: t('b.payment.view.payment_status_pending.text', 'Pending'),
    remitting_funds: t('b.payment.view.payment_status_pending.text', 'Pending'),
    fail: t('b.payment.view.payment_status_failed.text', 'Failed'),
    success: t('b.payment.view.successful.text', 'Successful')
  }
  const paymentColors = {
    upcoming: colors.regular,
    collecting_funds: colors.regular,
    remitting_funds: colors.regular,
    fail: '#FB554B',
    success: colors.green48
  }
  const { [payment_status]: statusColor } = paymentColors
  const firstPaymentDate = pad_agreement?.first_payment_on
  const priceDateOptions = getPriceDateOptions(t)
  const formattedDate = `price_date_${moment(firstPaymentDate).date()}`
  const selectedDateOption = find(propEq('value', formattedDate), priceDateOptions) || {
    label: ''
  }
  const processedDate = payment_proccess_at
    ? moment(payment_proccess_at, 'YYYY-MM-DD').format('MM-DD-YYYY')
    : ''

  return (
    <ContentWrapper top={46}>
      <AreaTitle>{t('b.payment.view.payment_summary.title', 'Payment Summary')}</AreaTitle>
      <StyledPanel hSpace={24} vSpace={24}>
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.status.label', 'Status')}</StyledLabel>
          <StyledValue color={statusColor}>{paymentStates[payment_status] ?? `-`}</StyledValue>
        </StyledRow>
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.confirmation_no.label', 'Confirmation No.')}</StyledLabel>
          <StyledValue>{transaction_number ?? `-`}</StyledValue>
        </StyledRow>
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.sent_by.label', 'Sent By')}</StyledLabel>
          <StyledValue>{getFullName(tenant)}</StyledValue>
        </StyledRow>
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.to.label', 'To')}</StyledLabel>
          <StyledValue>{getFullName(user)}</StyledValue>
        </StyledRow>
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.detail.label', 'Detail')}</StyledLabel>
          <StyledValue>{description}</StyledValue>
        </StyledRow>
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.additional_fees.label', 'Payment Method')}</StyledLabel>
          <StyledValue>PAD (Pre-Authorized Debit)</StyledValue>
        </StyledRow>
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.payment_amount.label', 'Payment Amount')}</StyledLabel>
          <StyledValue>
            <CurrencyFormatter value={charged} showCents />
          </StyledValue>
        </StyledRow>
        <StyledRow bottom={20}>
          <StyledLabel>
            {t('b.payment.view.payment_frequency.label', 'Payment Frequency')}
          </StyledLabel>
          <StyledValue>Monthly</StyledValue>
        </StyledRow>
        <StyledRow bottom={20}>
          <StyledLabel>{t('b.payment.view.payment_date.label', 'Payment Date')}</StyledLabel>
          <StyledValue>
            <StyledValue>{selectedDateOption?.label}</StyledValue>
          </StyledValue>
        </StyledRow>
        <StyledRow>
          <StyledLabel>
            {t('b.payment.view.payment_processed_date.label', 'Payment Processed Date')}
          </StyledLabel>
          <StyledValue>{processedDate}</StyledValue>
        </StyledRow>
      </StyledPanel>
    </ContentWrapper>
  )
}

export default PaymentSummary
